import { logging } from '@js-sdk/debug-tools';
import { StatusCode } from './enums/status_code';
/**
 * The amount of time needed before `ServerTime#getApproximate` triggers a
 * refresh of the server time.
 */
const REFRESH_THRESHOLD = 180000;
/**
 * The endpoint for the server time request.
 */
const ENDPOINT = '@webgate/melody/v1/time';
const debugLogger = logging.forTag('transport.server_time');
/**
 * The ServerTime class can be used to request the current timestamp as known
 * by the Spotify backend.
 *
 * This is useful in instances where multiple clients need to sync time. All
 * operations that deal with synchronization use the server's timestamp, which
 * are all synced across different services.
 *
 * @param transport - The transport instance that the ServerTime instance will
 *   use to make requests.
 */
export class ServerTime {
    constructor(transport) {
        /**
         * The last known server time as a timestamp.
         */
        this._serverTime = 0;
        /**
         * The timestamp when the server time was last received.
         */
        this._serverTimeFetchedAt = 0;
        this._transport = transport;
    }
    /**
     * Returns an instance of the ServerTime class.
     *
     * @param transport - The transport instance that the ServerTime instance
     *   will use to make requests.
     * @return A new ServerTime instance.
     */
    static create(transport) {
        return new ServerTime(transport);
    }
    /**
     * Parses the response of the ServerTime endpoint.
     *
     * @protected
     * @param requestStart - A timestamp that indicates when the request for
     *     the server time was made. This is used to calculate the latency that will
     *     be included in the final time value.
     * @param response - The response
     *     for the request to the endpoint.
     * @return The computed server time.
     */
    _parseServiceResponse(requestStart, response) {
        const requestEnd = Date.now();
        const status = response.status;
        if (status === StatusCode.OK) {
            this._serverTime =
                parseInt(response.body.timestamp, 10) +
                    // Assuming that the request and response times are approximately the
                    // same, the elapsed time on the server, after the server responded
                    // with the server time, must be half of the elapsed time on the client:
                    Math.round((requestEnd - requestStart) / 2);
        }
        else {
            debugLogger.error(`Cannot fetch server time. Service responded with ${status}`);
            this._serverTime = requestEnd;
        }
        this._serverTimeFetchedAt = requestEnd;
        return this._serverTime;
    }
    /**
     * Returns the current server time.
     *
     * @return The server time.
     */
    get() {
        const requestStart = Date.now();
        return this._transport
            .request(ENDPOINT, { responseType: 'json' })
            .then(this._parseServiceResponse.bind(this, requestStart));
    }
    /**
     * Returns the approximate server time.
     *
     * Unlike `ServerTime#get`, this method is not guaranteed to always request the
     * server time from the backend. Instead, it calculates the last known server
     * time and adds the time elapsed from when that was received up to the current
     * timestamp.
     *
     * @return The server time.
     */
    getApproximate() {
        if (!this._serverTime) {
            this.get();
            return Date.now();
        }
        const elapsed = Date.now() - this._serverTimeFetchedAt;
        if (elapsed > REFRESH_THRESHOLD) {
            this.get();
        }
        return this._serverTime + elapsed;
    }
}
//# sourceMappingURL=server_time.js.map